import ReactGA from 'react-ga4';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetIdentity } from 'react-admin';
import { Card, Box, Typography, Radio, RadioGroup, FormControlLabel, Button } from '@mui/material';
import { ErrorCode, Offerings, Package, PurchasesError } from '@revenuecat/purchases-js';
import { ProductOfferingCard } from './ProductOfferingCard';
import { useRevenueCat } from '../../payWall/RevenueCatProvider';
import { PeriodDurationType } from '../../payWall/paywall.model';
import { Link } from 'react-router-dom';

const styles = {
  pageTitle: { typography: 'h3', },
  sectionTitle: { typography: 'h6', },
  sectionSubtitle: { typography: 'body1', mt: 0 },
  sectionBody: { typography: 'body1', mb: 2, mt: 0, },
};

export const PricingPage = () => {

  const purchasesRef = useRef<HTMLElement | null>(null);
  const [offerings, setOfferings] = useState<Offerings | null>(null);
  const [duration, setDuration] = useState<PeriodDurationType>('monthly');

  const { identity, isLoading: identityLoading } = useGetIdentity();
  const { purchases, customerInfo, refreshCustomerInfo } = useRevenueCat();

  const subscribed = useMemo(() => customerInfo?.activeSubscriptions?.size ? customerInfo?.activeSubscriptions.size > 0 : false, [customerInfo]);

  const subscriptionTitle = useMemo(() => {
    if (!subscribed || !offerings) return null;
    const subscriptionCode = customerInfo?.activeSubscriptions.values().next().value;
    const matchedPackage =
      Object.values(offerings.all)
        .flatMap(offering => offering.availablePackages)
        .find(pkg => pkg.webBillingProduct.identifier === subscriptionCode);
    return matchedPackage?.webBillingProduct.title ?? null;
  }, [subscribed, offerings, customerInfo]);

  useEffect(() => {
    if (!purchases) return;
    const fetchOfferings = async () => {
      const offerings = await purchases.getOfferings();
      setOfferings(offerings);
    };
    fetchOfferings();
  }, [purchases]);

  const sortedOfferings = useMemo(() => {
    if (!offerings) return [];
    return Object.values(offerings.all).sort((a, b) => Number(a.metadata?.order || 0) - Number(b.metadata?.order || 0));
  }, [offerings]);

  const handleChangeDuration = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDuration(event.target.value as PeriodDurationType);
  };

  const handlePurchase = async (pkg: Package) => {
    try {
      const res = await purchases?.purchase({
        rcPackage: pkg,
        //customerEmail: appUserId,
        //htmlTarget: purchasesRef.current || undefined,
        skipSuccessPage: true,
        metadata: {
          userName: identity?.fullName || 'Anonymous',
        }
      });

      await purchases?.setAttributes({
        DisplayName: identity?.fullName || 'Anonymous',
      });

      // Send purchase event to Google Analytics
      ReactGA.event('purchase', {
        transaction_id: res?.storeTransaction.storeTransactionId || '', // or another unique ID
        value: pkg.webBillingProduct.price,
        currency: pkg.webBillingProduct.price.currency,
        items: [{
          item_id: pkg.webBillingProduct.identifier,
          item_name: pkg.webBillingProduct.title,
          price: pkg.webBillingProduct.price,
          quantity: 1,
        }]
      });

      await refreshCustomerInfo();

    } catch (e) {
      if (
        e instanceof PurchasesError &&
        e.errorCode == ErrorCode.UserCancelledError
      ) {
        // User cancelled the purchase process, don't do anything
        console.log('User cancelled the purchase process');
      } else {
        // Handle errors
        console.error('Error during purchase:', e);
      }
    };
  }

  if (!purchases) {
    return (
      <Card sx={{ padding: '20px', marginTop: 2 }}>
        <Typography sx={styles.sectionBody} gutterBottom>
          No purchases available, please check your account.
        </Typography>
      </Card>
    );
  }

  if (purchases && subscribed) {
    return (
      <Card sx={{ padding: '20px', marginTop: 2 }}>
        <Box display="flex" flexDirection='column' width={'100%'} justifyContent='center' alignItems='center'>
          {subscriptionTitle && (
            <Typography sx={styles.sectionTitle} gutterBottom>
              {`Enjoy your premium features with "${subscriptionTitle}" subscription!`}
            </Typography>
          )}
          <Button
            sx={{
              paddingBlock: 1.5,
              backgroundColor: 'primary.main',
              color: '#000000',
              border: '1px solid #FFFFFF',
              '&:hover': {
                backgroundColor: '#000000',
                color: '#FFFFFF',
              },
              textTransform: 'none'
            }}
            component={Link} to={customerInfo?.managementURL || '/pricing'}
          >
            Manage Subscription
          </Button>
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3, pt: 8, pb: 8 }}>

      <Box display="flex" flexDirection='column' gap={2}>
        <Box display="flex" width={'100%'} justifyContent='center' alignItems='center'>
          <Typography sx={styles.pageTitle} gutterBottom>
            {'Choose your pricing plan'}
          </Typography>
        </Box>

        <Box display='flex' flexDirection='row' justifyContent='center' alignItems='center' gap={2}>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue="monthly"
            name="radio-buttons-group"
            row
            value={duration}
            onChange={handleChangeDuration}
          >
            <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
            <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
            <FormControlLabel value="annual" control={<Radio />} label="Yearly" />
          </RadioGroup>
        </Box>

        <Box display='flex' flexDirection='row' gap={5} flexWrap='wrap' justifyContent='center'>
          {sortedOfferings.length === 0 && (<Typography sx={styles.sectionBody} gutterBottom> Loading available products...</Typography>)}
          {sortedOfferings.map((offering) => (
            <Box display='flex' flexDirection='row' key={offering.identifier} gap={2}>

              <ProductOfferingCard
                rcOffering={offering}
                duration={duration}
                onClick={handlePurchase}
                key={offering.identifier}
              />

            </Box>
          ))}
        </Box>
        <Box sx={{ width: '100%', height: '100%' }} ref={purchasesRef}></Box>
      </Box>

    </Card>
  );
};
